import React, { Suspense } from 'react';
import { FmStack, FmText } from '../FmComposition';
import { FmErrorBoundary } from '../FmErrorBoundary';
import type { FmDocumentElement } from './elements.types';

// pdf.js is large; only a view that places a served document downloads it.
const FmDocumentViewer = React.lazy(() => import('./FmDocumentViewer'));

/**
 * The document element: its title over a served PDF read page by page, or
 * over the reason the document is not available.
 */
export const FmDocumentElementView: React.FC<{
  element: FmDocumentElement;
}> = ({ element }) => {
  const testId = element.testId ?? 'story-document';
  return (
    <FmStack gap="sm" className="fm-element-document" data-testid={testId}>
      <FmText as="span" weight="medium">
        {element.title}
      </FmText>
      {element.file ? (
        // A pdf.js chunk or worker that fails to load is scoped to this element.
        <FmErrorBoundary
          title={element.labels.unreadable}
          resetKey={element.file}
          data-testid={`${testId}-unreadable`}
        >
          <Suspense
            fallback={
              <div className="fm-element-unavailable" data-state="loading" aria-busy>
                {element.labels.loading}
              </div>
            }
          >
            {/* A new file starts again on its first page. */}
            <FmDocumentViewer
              key={element.file}
              file={element.file}
              title={element.title}
              labels={element.labels}
              testId={testId}
            />
          </Suspense>
        </FmErrorBoundary>
      ) : (
        <div
          className="fm-element-unavailable"
          data-state="empty"
          data-testid={`${testId}-unavailable`}
        >
          {element.unavailableReason}
        </div>
      )}
      {element.caption ? (
        <FmText size="caption" tone="muted">
          {element.caption}
        </FmText>
      ) : null}
    </FmStack>
  );
};
